export type EEGPolarityConvention = "negative-up" | "positive-up";

export function calculatePolarityMeasurement(input: {
  startY: number;
  endY: number;
  channelHeight: number;
  sensitivity: number;
  polarityConvention: EEGPolarityConvention;
}): {
  amplitude: number;
  signedAmplitude: number;
  polarityLabel: "Negative" | "Positive" | "Flat";
} {
  const amplitudePixels = Math.abs(input.endY - input.startY);
  const amplitude =
    (amplitudePixels / input.channelHeight) * input.sensitivity * 1000;
  const conventionMultiplier =
    input.polarityConvention === "negative-up" ? -1 : 1;
  const signedPixels = (input.endY - input.startY) * conventionMultiplier;
  const signedAmplitude =
    (signedPixels / input.channelHeight) * input.sensitivity * 1000;
  const polarityLabel =
    Math.abs(signedAmplitude) < 0.01
      ? "Flat"
      : signedAmplitude < 0
        ? "Negative"
        : "Positive";

  return { amplitude, signedAmplitude, polarityLabel };
}

export function findPeakSampleIndex(
  data: ArrayLike<number>,
  centerIndex: number,
  windowSamples: number,
): number {
  if (data.length === 0) {
    return 0;
  }
  const clampedCenter = Math.max(0, Math.min(data.length - 1, centerIndex));
  const radius = Math.max(1, Math.floor(windowSamples));
  const start = Math.max(0, clampedCenter - radius);
  const end = Math.min(data.length - 1, clampedCenter + radius);
  let bestIndex = clampedCenter;
  let bestAbs = Math.abs(data[clampedCenter] ?? 0);
  for (let index = start; index <= end; index++) {
    const magnitude = Math.abs(data[index] ?? 0);
    if (magnitude > bestAbs) {
      bestAbs = magnitude;
      bestIndex = index;
    }
  }
  return bestIndex;
}

export function calculateCaliperMetrics(input: {
  startTimeMs: number;
  endTimeMs: number;
  startY: number;
  endY: number;
  startChannel: number;
  endChannel: number;
  channelHeight: number;
  sensitivity: number;
  polarityConvention: EEGPolarityConvention;
}): {
  durationMs: number;
  latencyMs: number;
  amplitudeUv: number;
  signedAmplitudeUv: number;
  polarityLabel: "Negative" | "Positive" | "Flat";
  frequencyHz: number | undefined;
  slopeUvPerMs: number | undefined;
  isInterchannel: boolean;
} {
  const durationMs = Math.abs(input.endTimeMs - input.startTimeMs);
  const polarity = calculatePolarityMeasurement({
    startY: input.startY,
    endY: input.endY,
    channelHeight: input.channelHeight,
    sensitivity: input.sensitivity,
    polarityConvention: input.polarityConvention,
  });
  const isInterchannel = input.startChannel !== input.endChannel;
  const frequencyHz = durationMs > 0 ? 1000 / durationMs : undefined;
  const slopeUvPerMs =
    !isInterchannel && durationMs > 0
      ? polarity.signedAmplitude / durationMs
      : undefined;

  return {
    durationMs,
    latencyMs: durationMs,
    amplitudeUv: polarity.amplitude,
    signedAmplitudeUv: polarity.signedAmplitude,
    polarityLabel: polarity.polarityLabel,
    frequencyHz,
    slopeUvPerMs,
    isInterchannel,
  };
}
